import * as RadixTooltip from "@radix-ui/react-tooltip";
import { css, styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import type { AppointmentStates } from "@acto/taskpane/core/domain";

type StateKey = "planned" | "noReport" | "documented" | "didNotTakePlace";

const STATE_ORDER: StateKey[] = ["planned", "noReport", "documented", "didNotTakePlace"];

const Trigger = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[8],
  cursor: "pointer",
}));

const Bar = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[2],
  height: theme.managementView.spacing[16],
  width: "100%",
}));

const Segment = styled("span")(({ theme }) => ({
  height: theme.managementView.spacing[4],
  flexBasis: 0,
  "&:first-of-type": {
    borderTopLeftRadius: theme.managementView.radius[4],
    borderBottomLeftRadius: theme.managementView.radius[4],
  },
  "&:last-of-type": {
    borderTopRightRadius: theme.managementView.radius[4],
    borderBottomRightRadius: theme.managementView.radius[4],
  },
}));

const tooltipContent = css(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[10],
  minWidth: "220px",
  padding: theme.managementView.spacing[10],
  borderRadius: theme.managementView.radius[20],
  backgroundColor: theme.managementView.colors.inverse.surface,
  filter: "drop-shadow(-8px 0 29px rgba(0, 0, 0, 0.06))",
  fontFamily: theme.managementView.font.familySecondary,
  fontWeight: theme.managementView.font.weight[500],
  zIndex: 10010,
}));

const TooltipTitle = styled("p")(({ theme }) => ({
  margin: 0,
  fontSize: theme.managementView.font.size[17],
  lineHeight: 1.48,
  color: theme.managementView.colors.inverse[94],
}));

const TooltipDivider = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  height: theme.managementView.spacing[16],
  "&::after": {
    content: '""',
    flex: 1,
    height: "1px",
    backgroundColor: theme.managementView.colors.inverse[8],
  },
}));

const StateList = styled("ul")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[12],
  margin: 0,
  padding: 0,
  listStyle: "none",
}));

const StateRow = styled("li")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[12],
  fontSize: theme.managementView.font.size[14],
  lineHeight: "20px",
  letterSpacing: theme.managementView.font.letterSpacing.tight,
}));

const StateDot = styled("span")({
  width: "11px",
  height: "11px",
  borderRadius: "50%",
  flexShrink: 0,
});

const StateLabel = styled("span")(({ theme }) => ({
  flex: 1,
  whiteSpace: "nowrap",
  color: theme.managementView.colors.inverse[80],
}));

const StateCount = styled("span")(({ theme }) => ({
  color: theme.managementView.colors.inverse[90],
}));

const stateColor: Record<StateKey, string> = {
  planned: css(({ theme }) => ({ backgroundColor: theme.managementView.colors.appointmentState.planned })),
  noReport: css(({ theme }) => ({ backgroundColor: theme.managementView.colors.appointmentState.noReport })),
  documented: css(({ theme }) => ({ backgroundColor: theme.managementView.colors.appointmentState.documented })),
  didNotTakePlace: css(({ theme }) => ({
    backgroundColor: theme.managementView.colors.appointmentState.didNotTakePlace,
  })),
};

interface AppointmentStatesCellProps {
  states: AppointmentStates;
}

export const AppointmentStatesCell = ({ states }: AppointmentStatesCellProps) => {
  const t = useTranslations("team.appointmentStates");
  const total = states.total;
  const labels: Record<StateKey, string> = {
    planned: t("planned"),
    noReport: t("noReport"),
    documented: t("documented"),
    didNotTakePlace: t("didNotTakePlace"),
  };

  if (total === 0) return <>{total}</>;

  return (
    <RadixTooltip.Provider delayDuration={200}>
      <RadixTooltip.Root>
        <RadixTooltip.Trigger asChild>
          <Trigger tabIndex={0}>
            {total}
            <Bar aria-hidden>
              {STATE_ORDER.filter((key) => states[key] > 0).map((key) => (
                <Segment key={key} className={stateColor[key]} style={{ flexGrow: states[key] }} />
              ))}
            </Bar>
          </Trigger>
        </RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content className={tooltipContent} side="top" sideOffset={8}>
            <TooltipTitle>{t("title", { count: total })}</TooltipTitle>
            <TooltipDivider />
            <StateList>
              {STATE_ORDER.map((key) => (
                <StateRow key={key}>
                  <StateDot className={stateColor[key]} />
                  <StateLabel>{labels[key]}</StateLabel>
                  <StateCount>{states[key]}</StateCount>
                </StateRow>
              ))}
            </StateList>
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
};
